import React from "react";
import UserModel from "./types";
import { format, isValid, parseISO } from "date-fns";

interface ProfileHeaderProps {
  profileData: UserModel;
  isEmployee: boolean;
  isEditing: boolean;
  onToggleEdit: () => void;
}

const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  profileData,
  isEmployee,
  isEditing,
  onToggleEdit,
}) => {
  // Safe date formatting function
  const formatSafeDate = (
    dateString: string,
    formatStr: string = "MMM yyyy"
  ) => {
    try {
      // First try to parse as ISO date
      const parsedDate = parseISO(dateString);
      if (isValid(parsedDate)) {
        return format(parsedDate, formatStr);
      }

      // If that fails, try creating a new Date
      const fallbackDate = new Date(dateString);
      if (isValid(fallbackDate)) {
        return format(fallbackDate, formatStr);
      }

      // If all fails, return a placeholder
      return "N/A";
    } catch (error) {
      console.error("Date formatting error:", error);
      return "N/A";
    }
  };

  // Get initials for the avatar
  const getInitials = () => {
    const firstInitial = profileData.firstName
      ? profileData.firstName.charAt(0).toUpperCase()
      : "?";
    const lastInitial = profileData.lastName
      ? profileData.lastName.charAt(0).toUpperCase()
      : "";
    return firstInitial + lastInitial;
  };

  // Use current date as fallback for "Member Since" if birthDate is invalid
  const memberSinceDisplay = profileData.birthDate
    ? formatSafeDate(profileData.birthDate)
    : format(new Date(), "MMM yyyy");

  return (
    <div className="bg-gray-800 rounded-lg p-6 flex flex-col md:flex-row items-center md:items-start gap-8">
      <div className="w-32 h-32 rounded-full bg-cannabis-green-500 flex items-center justify-center text-white text-4xl font-bold">
        {getInitials()}
      </div>

      <div className="flex-1">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold">
              {profileData.firstName || "User"} {profileData.lastName || ""}
            </h2>
            <p className="text-gray-300">{profileData.email}</p>
          </div>

          <div className="mt-4 md:mt-0">
            <button
              onClick={onToggleEdit}
              className={`px-4 py-2 rounded-lg transition ${
                isEditing
                  ? "bg-gray-600 hover:bg-gray-700"
                  : "bg-cannabis-green-500 hover:bg-cannabis-green-600"
              }`}
            >
              {isEditing ? "Cancel Editing" : "Edit Profile"}
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-4">
          {/* <div className="bg-gray-700 px-4 py-2 rounded-lg">
            <p className="text-sm text-gray-400">Member Since</p>
            <p>{memberSinceDisplay}</p>
          </div> */}

          {isEmployee && (
            <div className="bg-cannabis-green-900 px-4 py-2 rounded-lg">
              <p className="text-sm text-cannabis-green-300">Staff Account</p>
              <p className="text-cannabis-green-100">Employee</p>
            </div>
          )}

          <div className="bg-gray-700 px-4 py-2 rounded-lg">
            <p className="text-sm text-gray-400">Phone</p>
            <p>{profileData.phone || "Not provided"}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;
